import { api } from "@breakout/backend/convex/_generated/api"
import { prepareSell } from "@breakout/sdk"
import { useWallets } from "@privy-io/react-auth"
import { useMutation } from "@tanstack/react-query"
import { useAction } from "convex/react"
import { useCallback, useState } from "react"
import type { Address } from "viem"
import { getAddress, isAddress } from "viem"
import { useBreakoutConfig } from "../../lib/providers"
import { captureTradeError } from "../../lib/sentry"
import { DEFAULT_DEADLINE_SECONDS, serializeSellPreparation } from "../../lib/tradeUtils"
import { createWalletClientFromWallet, findEmbeddedWallet } from "../../lib/wallet"
import { useInvalidateAfterTransaction } from "./useInvalidateAfterTransaction"

type TradeStatus = "idle" | "processing" | "confirmed" | "error"

interface SellParams {
	marketAddress: Address
	bucket: number
	shares: number
	slippageBps?: number
}

interface TradeResult {
	success: boolean
	transactionHash: string
	blockNumber?: number
}

export function useSell() {
	const config = useBreakoutConfig()
	const { wallets } = useWallets()
	const [status, setStatus] = useState<TradeStatus>("idle")
	const executeGaslessSell = useAction(api.domains.trading.actions.executeGaslessSell)
	const { invalidateAfterTrade } = useInvalidateAfterTransaction()

	const mutation = useMutation({
		mutationFn: async ({
			marketAddress,
			bucket,
			shares,
			slippageBps,
		}: SellParams): Promise<TradeResult> => {
			const wallet = findEmbeddedWallet(wallets)
			if (!wallet) {
				throw new Error("No wallet connected")
			}

			const walletClient = await createWalletClientFromWallet({ wallet })
			if (!isAddress(wallet.address)) {
				throw new Error("Invalid wallet address")
			}
			const userAddress = getAddress(wallet.address)

			// 1. Prepare the trade (SDK validates user has sufficient shares)
			setStatus("processing")

			const preparation = await prepareSell(config.publicClient, {
				market: marketAddress,
				user: userAddress,
				bucket,
				shares,
				chainId: config.chainId,
				slippageBps,
				deadlineSeconds: DEFAULT_DEADLINE_SECONDS,
			})

			// 2. Sign native sellWithSignature payload (silently with embedded wallet)
			const signature = await walletClient.signTypedData({
				account: userAddress,
				domain: preparation.signingPayload.domain,
				types: preparation.signingPayload.types,
				primaryType: preparation.signingPayload.primaryType,
				message: preparation.signingPayload.message,
			})

			// 3. Execute the trade via gasless relayer
			const result = await executeGaslessSell({
				preparation: serializeSellPreparation(preparation),
				signature,
			})

			setStatus("confirmed")
			return {
				success: true,
				transactionHash: result.txHash,
				blockNumber: result.blockNumber,
			}
		},
		onSuccess: (_, variables) => {
			const wallet = findEmbeddedWallet(wallets)
			const userAddress =
				wallet?.address && isAddress(wallet.address) ? getAddress(wallet.address) : undefined
			invalidateAfterTrade({
				marketAddress: variables.marketAddress,
				userAddress,
			})
		},
		onError: (error, variables) => {
			setStatus("error")
			const wallet = findEmbeddedWallet(wallets)
			if (error instanceof Error) {
				captureTradeError(error, {
					action: "sell",
					marketAddress: variables.marketAddress,
					bucket: variables.bucket,
					shares: variables.shares.toString(),
					walletAddress: wallet?.address,
				})
				console.error("[useSell]", error)
			}
		},
		onSettled: () => {
			setTimeout(() => setStatus("idle"), 2000)
		},
	})

	const reset = useCallback(() => {
		setStatus("idle")
		mutation.reset()
	}, [mutation])

	return {
		...mutation,
		status,
		reset,
	}
}
